"use client"

import { SectionHeader } from "@/components/marketplace/layout/section-header"
import { CarouselDots, Reel, Stars, Text } from "@/components/ui"
import { Link } from "@/i18n/navigation"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"

export interface Quote {
  name: string
  loc: string
  text: string
  stars: number
  image?: string
}

interface TestimonialsProps {
  quotes: Quote[]
  title?: string
  eyebrow?: string
  sub?: string
  cta?: string
  ctaHref?: string
}

// A swipeable rail of reviews. The masthead carries the title and a review link
// on its baseline; the cards bleed to the gutter and page under dots. Stars are
// brand blue, and each card is its own lifted surface — no avatars, just the
// quote and who said it.
export const Testimonials = ({ quotes, title, eyebrow, sub, cta, ctaHref }: TestimonialsProps) => {
  const { ref, index, overflowing, goTo } = useReelPagination(quotes.length)
  return (
    <div className="@container">
      <div className="px-gut mb-500 flex flex-col gap-300 @3xl:mb-750">
        <SectionHeader
          title={title ?? ""}
          eyebrow={eyebrow}
          action={
            cta ? (
              <Link
                href={ctaHref ?? "#"}
                className="text-brand hover:text-brand-hover text-body font-text focus-visible:ring-brand hit-target @3xl:text-body-lg inline-flex shrink-0 items-center underline underline-offset-4 focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-none"
              >
                {cta}
              </Link>
            ) : undefined
          }
        />
        {sub && (
          <Text as="p" variant="bodyLg" family="text" tone="muted" numeric className="max-w-measure">
            {sub}
          </Text>
        )}
      </div>
      <Reel ref={ref} gap="250" snap inset className="items-stretch @3xl:gap-500">
        {quotes.map((quote, i) => (
          <article
            key={`${quote.name}-${i}`}
            style={{ animationDelay: `${Math.min(i, 6) * 70}ms` }}
            className="motion-safe:animate-rise-in rounded-10x bg-surface border-line-subtle flex w-71 shrink-0 flex-col gap-350 border p-350 @3xl:w-103 @3xl:gap-500 @3xl:p-500"
          >
            <Stars value={quote.stars} size={16} className="text-brand" />
            <Text as="p" variant="body" family="text" tone="strong" className="flex-1 font-normal text-pretty">
              {quote.text}
            </Text>
            <Text as="p" variant="body" family="text" tone="muted">
              <span className="text-fg-strong font-medium">{quote.name}</span>
              {quote.loc ? `, ${quote.loc}` : ""}
            </Text>
          </article>
        ))}
      </Reel>
      {overflowing && (
        <div className="mt-500 flex justify-center @3xl:mt-750">
          <CarouselDots count={quotes.length} index={index} onChange={goTo} tone="plain" activeColor="bg-brand" />
        </div>
      )}
    </div>
  )
}
